/**
 * Phase 4A.5A — the ONLY source of an Assistant page hint.
 *
 * The hint is a *hint*: the backend re-derives everything that matters from the
 * caller's JWT and `X-Organization-ID`, and `context_builder` normalizes an
 * unrecognized `page_type` to `unknown` and a non-UUID `object_id` to nothing.
 * This module exists so the browser still sends only values it can justify —
 * derived from the router's own path, never from a query string, a hash, a
 * `postMessage`, page content or anything a user typed.
 *
 * The page vocabulary is a CLOSED allowlist copied from the backend's
 * `quowada.assistant.knowledge_source.PAGES`. A route this module does not
 * recognize produces no hint at all, rather than a guessed or path-derived
 * label: a wrong hint would silently steer which evidence applies.
 */

/** Exactly the backend's `PAGES`. Never widened by string manipulation. */
export const ASSISTANT_PAGE_TYPES = Object.freeze([
  "dashboard",
  "quotations_list",
  "quotation_new",
  "quotation_detail",
  "customers",
  "profile",
  "settings",
  "organization_users",
  "notifications",
]);

// The canonical lowercase 8-4-4-4-12 form. Deliberately strict: a value that is
// not already canonical is not repaired here, because the id that gets
// authorized must be the id that was sent (the backend applies the same rule).
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// Static route table, in match order. Each entry owns its own page type; there
// is no fallback that turns an unknown path into a plausible-looking label.
const ROUTES = [
  { re: /^\/dashboard$/, page: "dashboard" },
  { re: /^\/quotations$/, page: "quotations_list" },
  { re: /^\/quotations\/new$/, page: "quotation_new" },
  { re: /^\/quotations\/([^/]+)$/, page: "quotation_detail", object: true },
  { re: /^\/customers$/, page: "customers" },
  { re: /^\/profile$/, page: "profile" },
  { re: /^\/settings$/, page: "settings" },
  { re: /^\/organization\/users$/, page: "organization_users" },
  { re: /^\/notifications$/, page: "notifications" },
];

/** Drop a single trailing slash so `/customers/` and `/customers` agree. */
function normalizePath(pathname) {
  if (typeof pathname !== "string" || !pathname.startsWith("/")) return null;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return path === "" ? "/" : path;
}

/**
 * The page hint for one router pathname, or `null` for an unrecognized route.
 *
 * Returns the exact wire shape the backend schema permits — `page_type` and
 * `object_id`, nothing else. `object_id` is `null` unless the route genuinely
 * carries a canonical quotation UUID.
 */
export function pageHintFromPath(pathname) {
  const path = normalizePath(pathname);
  if (path === null) return null;
  for (const route of ROUTES) {
    const match = route.re.exec(path);
    if (!match) continue;
    let objectId = null;
    if (route.object) {
      const raw = match[1];
      if (UUID_RE.test(raw)) objectId = raw;
    }
    return { page_type: route.page, object_id: objectId };
  }
  return null;
}

export default pageHintFromPath;
